import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './AdminPanel.css';

const LICENSE_API = process.env.REACT_APP_LICENSE_API || 'https://glitchcloud.app/license-api';
const ADMIN_KEY = 'glitch-admin-2024-secure-key';

interface VpsEntry { ip: string; port: string }

interface LicenseRecord {
  id: string;
  code: string;
  type: string;
  username?: string;
  valid: boolean;
  isValid?: boolean;
  expiryDate?: string;
  vpsIp?: string;
  vpsPort?: string;
  vpsIp2?: string;
  vpsPort2?: string;
  vpsList?: VpsEntry[];
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
  currentLevel?: number;
  totalXp?: number;
  currentLevelXp?: number;
  nextLevelXp?: number;
  rank?: string;
  totalScan?: number;
  telegramUserId?: string;
  telegram_id?: string;
  telegramPhotoUrl?: string;
  isAdmin?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Stats {
  total: number;
  active: number;
  expired: number;
  byType: Record<string, number>;
  totalXp: number;
  totalScans: number;
}

const RANK_COLOR: Record<string, string> = {
  injected: '#E8132E',
  breached: '#f59e0b',
  ghosted: '#a855f7',
};

const TYPE_COLOR: Record<string, string> = {
  premium: '#38bdf8',
  lifetime: '#f59e0b',
  trial: '#a855f7',
  new: '#737373',
};

function computeStats(licenses: LicenseRecord[]): Stats {
  const now = new Date();
  let active = 0, expired = 0, totalXp = 0, totalScans = 0;
  const byType: Record<string, number> = {};
  for (const l of licenses) {
    const isExp = l.expiryDate ? new Date(l.expiryDate) < now : false;
    const isValid = (l.valid !== false && l.isValid !== false) && !isExp;
    if (isValid) active++; else expired++;
    const t = (l.type || 'unknown').toLowerCase();
    byType[t] = (byType[t] || 0) + 1;
    totalXp += l.totalXp || 0;
    totalScans += l.totalScan || 0;
  }
  return { total: licenses.length, active, expired, byType, totalXp, totalScans };
}

function fmtN(n: number) { return n.toLocaleString('en-US'); }

function relativeTime(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
  </svg>
);

// ── Edit modal ─────────────────────────────────────────────────
interface EditModalProps {
  license: LicenseRecord;
  onClose: () => void;
  onSave: (id: string, updates: Partial<LicenseRecord>) => Promise<void>;
}

const EMPTY_VPS: VpsEntry = { ip: '', port: '3007' };

const EditModal: React.FC<EditModalProps> = ({ license, onClose, onSave }) => {
  const [form, setForm] = useState<LicenseRecord>({ ...license });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const set = (k: keyof LicenseRecord, v: any) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(license.id, form);
      setMsg({ type: 'ok', text: 'Saved' });
      setTimeout(onClose, 800);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const vpsListEntries: VpsEntry[] = Array.isArray(form.vpsList) && form.vpsList.length > 0
    ? form.vpsList
    : [];

  const updateVpsEntry = (idx: number, field: keyof VpsEntry, val: string) => {
    const updated = [...vpsListEntries];
    updated[idx] = { ...updated[idx], [field]: val };
    set('vpsList', updated);
  };
  const addVps = () => set('vpsList', [...vpsListEntries, { ...EMPTY_VPS }]);
  const removeVps = (idx: number) => set('vpsList', vpsListEntries.filter((_, i) => i !== idx));

  return (
    <motion.div
      className="apm-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="apm-modal"
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.2 }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="apm-modal-header">
          <div className="apm-modal-title-row">
            <ShieldIcon />
            <span className="apm-modal-title">Edit License</span>
            <span className="apm-modal-subtitle">{license.code}</span>
          </div>
          <button className="apm-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="apm-modal-body">
          {/* Identity */}
          <div className="apm-modal-section">
            <p className="apm-modal-section-label">Identity</p>
            <div className="apm-modal-grid">
              <div className="apm-field">
                <label className="apm-label">Username</label>
                <input className="apm-input" value={form.username || ''} onChange={e => set('username', e.target.value)} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Type</label>
                <select className="apm-input" value={form.type || 'new'} onChange={e => set('type', e.target.value)}>
                  <option value="new">New</option>
                  <option value="trial">Trial</option>
                  <option value="premium">Premium</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div className="apm-field">
                <label className="apm-label">Expiry Date</label>
                <input className="apm-input" type="date"
                  value={form.expiryDate ? form.expiryDate.slice(0, 10) : ''}
                  onChange={e => set('expiryDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                />
              </div>
              <div className="apm-field apm-field--check">
                <label className="apm-label">Status</label>
                <label className="apm-toggle">
                  <input type="checkbox" checked={form.valid !== false} onChange={e => set('valid', e.target.checked)} />
                  <span className="apm-toggle-track" />
                  <span className="apm-toggle-label">{form.valid !== false ? 'Active' : 'Disabled'}</span>
                </label>
              </div>
              <div className="apm-field apm-field--check">
                <label className="apm-label">Admin</label>
                <label className="apm-toggle">
                  <input type="checkbox" checked={!!form.isAdmin} onChange={e => set('isAdmin', e.target.checked)} />
                  <span className="apm-toggle-track apm-toggle-track--red" />
                  <span className="apm-toggle-label">{form.isAdmin ? 'Yes' : 'No'}</span>
                </label>
              </div>
            </div>
          </div>

          {/* XP / Level / Rank */}
          <div className="apm-modal-section">
            <p className="apm-modal-section-label">Progression</p>
            <div className="apm-modal-grid">
              <div className="apm-field">
                <label className="apm-label">Level</label>
                <input className="apm-input" type="number" min={1} value={form.currentLevel ?? 1}
                  onChange={e => set('currentLevel', Number(e.target.value))} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Total XP</label>
                <input className="apm-input" type="number" min={0} value={form.totalXp ?? 0}
                  onChange={e => set('totalXp', Number(e.target.value))} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Level XP</label>
                <input className="apm-input" type="number" min={0} value={form.currentLevelXp ?? 0}
                  onChange={e => set('currentLevelXp', Number(e.target.value))} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Next Level XP</label>
                <input className="apm-input" type="number" min={1} value={form.nextLevelXp ?? 250}
                  onChange={e => set('nextLevelXp', Number(e.target.value))} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Rank</label>
                <select className="apm-input" value={(form.rank || 'ghosted').toLowerCase()}
                  onChange={e => set('rank', e.target.value)}>
                  <option value="ghosted">Ghosted</option>
                  <option value="breached">Breached</option>
                  <option value="injected">Injected</option>
                </select>
              </div>
              <div className="apm-field">
                <label className="apm-label">Total Scans</label>
                <input className="apm-input" type="number" min={0} value={form.totalScan ?? 0}
                  onChange={e => set('totalScan', Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Legacy VPS */}
          <div className="apm-modal-section">
            <p className="apm-modal-section-label">Legacy VPS</p>
            <div className="apm-modal-grid">
              <div className="apm-field">
                <label className="apm-label">VPS IP</label>
                <input className="apm-input" value={form.vpsIp || ''} onChange={e => set('vpsIp', e.target.value)} placeholder="1.2.3.4" />
              </div>
              <div className="apm-field">
                <label className="apm-label">VPS Port</label>
                <input className="apm-input" value={form.vpsPort || ''} onChange={e => set('vpsPort', e.target.value)} placeholder="3007" />
              </div>
              <div className="apm-field">
                <label className="apm-label">VPS IP 2</label>
                <input className="apm-input" value={form.vpsIp2 || ''} onChange={e => set('vpsIp2', e.target.value)} placeholder="1.2.3.4" />
              </div>
              <div className="apm-field">
                <label className="apm-label">VPS Port 2</label>
                <input className="apm-input" value={form.vpsPort2 || ''} onChange={e => set('vpsPort2', e.target.value)} placeholder="3007" />
              </div>
            </div>
          </div>

          {/* VPS List */}
          <div className="apm-modal-section">
            <div className="apm-modal-section-head">
              <p className="apm-modal-section-label">VPS List</p>
              <button className="apm-btn-mini" onClick={addVps}><PlusIcon /> Add</button>
            </div>
            {vpsListEntries.map((v, idx) => (
              <div key={idx} className="apm-vps-row">
                <span className="apm-vps-idx">S{idx + 1}</span>
                <input className="apm-input apm-input--mono" value={v.ip} onChange={e => updateVpsEntry(idx, 'ip', e.target.value)} placeholder="IP" />
                <input className="apm-input apm-input--mono apm-input--port" value={v.port} onChange={e => updateVpsEntry(idx, 'port', e.target.value)} placeholder="3007" />
                <button className="apm-icon-btn apm-icon-btn--danger" onClick={() => removeVps(idx)}><TrashIcon /></button>
              </div>
            ))}
            {vpsListEntries.length === 0 && <p className="apm-vps-empty">No VPS entries — using legacy fields above</p>}
          </div>

          {/* Add-ons */}
          <div className="apm-modal-section">
            <p className="apm-modal-section-label">Add-ons</p>
            <div className="apm-addons-grid">
              {(['grabber', 'mailer', 'debouncer', 'stripe', 'premiumProxy'] as const).map(key => (
                <label key={key} className="apm-addon-toggle">
                  <input type="checkbox" checked={!!(form as any)[key]}
                    onChange={e => set(key as keyof LicenseRecord, e.target.checked)} />
                  <span className="apm-addon-track" />
                  <span className="apm-addon-label">{key}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="apm-modal-footer">
          {msg && <span className={`apm-msg${msg.type === 'err' ? ' apm-msg--err' : ''}`}>{msg.text}</span>}
          <div className="apm-modal-footer-actions">
            <button className="apm-btn apm-btn--ghost" onClick={onClose}>Cancel</button>
            <button className="apm-btn apm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Create modal ───────────────────────────────────────────────
interface CreateModalProps {
  onClose: () => void;
  onCreate: (data: Partial<LicenseRecord>) => Promise<void>;
}

const CreateModal: React.FC<CreateModalProps> = ({ onClose, onCreate }) => {
  const [form, setForm] = useState<Partial<LicenseRecord>>({
    code: '',
    type: 'premium',
    username: '',
    valid: true,
    vpsIp: '',
    vpsPort: '3007',
    grabber: false,
    mailer: false,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const set = (k: keyof LicenseRecord, v: any) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.code?.trim()) { setMsg({ type: 'err', text: 'Code required' }); return; }
    setSaving(true);
    try {
      await onCreate(form);
      setMsg({ type: 'ok', text: 'Created' });
      setTimeout(onClose, 700);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Create failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div className="apm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="apm-modal"
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.2 }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="apm-modal-header">
          <div className="apm-modal-title-row"><PlusIcon /><span className="apm-modal-title">New License</span></div>
          <button className="apm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="apm-modal-body">
          <div className="apm-modal-section">
            <div className="apm-modal-grid">
              <div className="apm-field apm-field--full">
                <label className="apm-label">License Code *</label>
                <input className="apm-input apm-input--mono" value={form.code || ''} onChange={e => set('code', e.target.value)} placeholder="GLITCH-XXXX-XXXX" />
              </div>
              <div className="apm-field">
                <label className="apm-label">Username</label>
                <input className="apm-input" value={form.username || ''} onChange={e => set('username', e.target.value)} />
              </div>
              <div className="apm-field">
                <label className="apm-label">Type</label>
                <select className="apm-input" value={form.type || 'premium'} onChange={e => set('type', e.target.value)}>
                  <option value="new">New</option>
                  <option value="trial">Trial</option>
                  <option value="premium">Premium</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div className="apm-field">
                <label className="apm-label">VPS IP</label>
                <input className="apm-input apm-input--mono" value={form.vpsIp || ''} onChange={e => set('vpsIp', e.target.value)} placeholder="1.2.3.4" />
              </div>
              <div className="apm-field">
                <label className="apm-label">VPS Port</label>
                <input className="apm-input apm-input--mono" value={form.vpsPort || ''} onChange={e => set('vpsPort', e.target.value)} placeholder="3007" />
              </div>
              <div className="apm-field">
                <label className="apm-label">Expiry Date</label>
                <input className="apm-input" type="date" onChange={e => set('expiryDate', e.target.value ? new Date(e.target.value).toISOString() : '')} />
              </div>
            </div>
          </div>
        </div>
        <div className="apm-modal-footer">
          {msg && <span className={`apm-msg${msg.type === 'err' ? ' apm-msg--err' : ''}`}>{msg.text}</span>}
          <div className="apm-modal-footer-actions">
            <button className="apm-btn apm-btn--ghost" onClick={onClose}>Cancel</button>
            <button className="apm-btn apm-btn--primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Delete confirm modal ───────────────────────────────────────
interface DeleteModalProps {
  license: LicenseRecord;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ license, onClose, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  return (
    <motion.div className="apm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="apm-modal apm-modal--sm" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="apm-modal-header">
          <span className="apm-modal-title apm-modal-title--danger">Delete License</span>
          <button className="apm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="apm-modal-body">
          <p className="apm-delete-warn">This will permanently delete license <code className="apm-code">{license.code}</code>. This cannot be undone.</p>
          {license.username && <p className="apm-delete-user">User: @{license.username}</p>}
        </div>
        <div className="apm-modal-footer apm-modal-footer--end">
          <button className="apm-btn apm-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="apm-btn apm-btn--danger" onClick={async () => { setDeleting(true); await onConfirm(); setDeleting(false); }} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Main AdminPanel ────────────────────────────────────────────
const AdminPanel: React.FC = () => {
  const [licenses, setLicenses] = useState<LicenseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortKey, setSortKey] = useState<keyof LicenseRecord>('createdAt');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const [editTarget, setEditTarget] = useState<LicenseRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LicenseRecord | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const adminHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ADMIN_KEY}` };

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${LICENSE_API}/licenses?admin_key=${ADMIN_KEY}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLicenses(Array.isArray(data) ? data : data.licenses || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  // Fetch Telegram photo for records that have a telegramUserId but no photo
  useEffect(() => {
    // Photos are fetched server-side; just display what's stored
  }, [licenses]);

  const handleSave = async (id: string, updates: Partial<LicenseRecord>) => {
    const res = await fetch(`${LICENSE_API}/licenses/${id}`, {
      method: 'PUT',
      headers: adminHeaders,
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    const updated = await res.json();
    setLicenses(prev => prev.map(l => l.id === id ? { ...l, ...updated } : l));
  };

  const handleCreate = async (data: Partial<LicenseRecord>) => {
    const res = await fetch(`${LICENSE_API}/licenses`, {
      method: 'POST',
      headers: adminHeaders,
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    const created = await res.json();
    setLicenses(prev => [created, ...prev]);
  };

  const handleDelete = async (id: string) => {
    const res = await fetch(`${LICENSE_API}/licenses/${id}`, { method: 'DELETE', headers: adminHeaders });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    setLicenses(prev => prev.filter(l => l.id !== id));
    setDeleteTarget(null);
  };

  // Filter + sort
  const now = new Date();
  const filtered = licenses.filter(l => {
    const isExp = l.expiryDate ? new Date(l.expiryDate) < now : false;
    const isActive = (l.valid !== false && l.isValid !== false) && !isExp;
    if (filterStatus === 'active' && !isActive) return false;
    if (filterStatus === 'inactive' && isActive) return false;
    if (filterType !== 'all' && (l.type || '').toLowerCase() !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.username || '').toLowerCase().includes(q)
        || (l.code || '').toLowerCase().includes(q)
        || (l.telegramUserId || '').includes(q)
        || (l.telegram_id || '').includes(q);
    }
    return true;
  }).sort((a, b) => {
    const av = (a as any)[sortKey] ?? '';
    const bv = (b as any)[sortKey] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortAsc ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const stats = computeStats(licenses);

  const handleSort = (k: keyof LicenseRecord) => {
    if (sortKey === k) setSortAsc(a => !a);
    else { setSortKey(k); setSortAsc(true); }
  };

  const SortIcon = ({ k }: { k: keyof LicenseRecord }) => (
    <span className="apm-sort-icon">{sortKey === k ? (sortAsc ? '↑' : '↓') : '·'}</span>
  );

  const allTypes = Array.from(new Set(licenses.map(l => (l.type || 'unknown').toLowerCase())));

  return (
    <div className="apm-page">
      {/* Header */}
      <div className="apm-header">
        <div className="apm-header-left">
          <div className="apm-header-icon"><ShieldIcon /></div>
          <div>
            <h1 className="apm-title">Admin Panel</h1>
            <p className="apm-subtitle">License management & user oversight</p>
          </div>
        </div>
        <div className="apm-header-right">
          <button className="apm-btn apm-btn--ghost apm-btn--icon" onClick={fetchLicenses} disabled={loading} title="Refresh">
            <RefreshIcon />
          </button>
          <button className="apm-btn apm-btn--primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New License
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="apm-stats-strip">
        <div className="apm-stat-card">
          <span className="apm-stat-label">Total</span>
          <span className="apm-stat-value">{fmtN(stats.total)}</span>
        </div>
        <div className="apm-stat-card apm-stat-card--green">
          <span className="apm-stat-label">Active</span>
          <span className="apm-stat-value">{fmtN(stats.active)}</span>
        </div>
        <div className="apm-stat-card apm-stat-card--red">
          <span className="apm-stat-label">Inactive</span>
          <span className="apm-stat-value">{fmtN(stats.expired)}</span>
        </div>
        <div className="apm-stat-card apm-stat-card--blue">
          <span className="apm-stat-label">Total XP</span>
          <span className="apm-stat-value">{fmtN(stats.totalXp)}</span>
        </div>
        <div className="apm-stat-card apm-stat-card--purple">
          <span className="apm-stat-label">Total Scans</span>
          <span className="apm-stat-value">{fmtN(stats.totalScans)}</span>
        </div>
        {Object.entries(stats.byType).map(([type, count]) => (
          <div key={type} className="apm-stat-card">
            <span className="apm-stat-label">{type}</span>
            <span className="apm-stat-value" style={{ color: TYPE_COLOR[type] || '#737373' }}>{fmtN(count)}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="apm-filters">
        <div className="apm-search-wrap">
          <svg className="apm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="apm-search"
            placeholder="Search by username, code, Telegram ID…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          {search && <button className="apm-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <div className="apm-filter-group">
          <select className="apm-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}>
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select className="apm-select" value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }}>
            <option value="all">All types</option>
            {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <span className="apm-count">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Error */}
      {error && (
        <div className="apm-error">{error} <button className="apm-retry" onClick={fetchLicenses}>Retry</button></div>
      )}

      {/* Table */}
      {loading ? (
        <div className="apm-loading">
          <div className="apm-spinner" />
          <p>Loading licenses…</p>
        </div>
      ) : (
        <div className="apm-table-wrap">
          <table className="apm-table">
            <thead>
              <tr>
                <th className="apm-th apm-th--user">User</th>
                <th className="apm-th apm-th--sortable" onClick={() => handleSort('code')}>Code <SortIcon k="code" /></th>
                <th className="apm-th apm-th--sortable" onClick={() => handleSort('type')}>Type <SortIcon k="type" /></th>
                <th className="apm-th apm-th--sortable" onClick={() => handleSort('currentLevel')}>Level <SortIcon k="currentLevel" /></th>
                <th className="apm-th apm-th--sortable" onClick={() => handleSort('totalXp')}>XP <SortIcon k="totalXp" /></th>
                <th className="apm-th">Rank</th>
                <th className="apm-th">Servers</th>
                <th className="apm-th apm-th--sortable" onClick={() => handleSort('expiryDate')}>Expiry <SortIcon k="expiryDate" /></th>
                <th className="apm-th">Status</th>
                <th className="apm-th apm-th--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr><td className="apm-td-empty" colSpan={10}>No licenses match your filters</td></tr>
              ) : pageItems.map(l => {
                const isExp = l.expiryDate ? new Date(l.expiryDate) < now : false;
                const isActive = (l.valid !== false && l.isValid !== false) && !isExp;
                const rank = (l.rank || 'ghosted').toLowerCase();
                const rankColor = RANK_COLOR[rank] || '#a855f7';
                const typeColor = TYPE_COLOR[(l.type || '').toLowerCase()] || '#737373';
                const tgId = l.telegramUserId || l.telegram_id || '';
                const vpsCount = (() => {
                  if (l.vpsList && l.vpsList.length > 0) return l.vpsList.length;
                  let c = 0;
                  if (l.vpsIp) c++;
                  if (l.vpsIp2) c++;
                  return c;
                })();
                return (
                  <tr key={l.id} className="apm-tr">
                    <td className="apm-td apm-td--user">
                      <div className="apm-user-cell">
                        {l.telegramPhotoUrl ? (
                          <img className="apm-avatar" src={l.telegramPhotoUrl} alt="" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="apm-avatar apm-avatar--fallback">
                            {(l.username || l.code || '?').slice(0, 2).toUpperCase()}
                          </span>
                        )}
                        <div className="apm-user-info">
                          <span className="apm-username">@{l.username || '—'}</span>
                          {tgId && <span className="apm-tgid">#{tgId}</span>}
                          {l.isAdmin && <span className="apm-admin-badge">ADMIN</span>}
                        </div>
                      </div>
                    </td>
                    <td className="apm-td"><code className="apm-code">{l.code}</code></td>
                    <td className="apm-td"><span className="apm-type-badge" style={{ color: typeColor, borderColor: `${typeColor}40`, background: `${typeColor}12` }}>{(l.type || '?').toUpperCase()}</span></td>
                    <td className="apm-td apm-td--num">{l.currentLevel ?? 1}</td>
                    <td className="apm-td apm-td--num">{fmtN(l.totalXp ?? 0)}</td>
                    <td className="apm-td">
                      <span className="apm-rank-dot" style={{ background: rankColor }} />
                      <span style={{ color: rankColor, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{rank}</span>
                    </td>
                    <td className="apm-td apm-td--center">
                      {vpsCount > 0 ? (
                        <span className="apm-vps-count">{vpsCount}</span>
                      ) : <span className="apm-null">—</span>}
                    </td>
                    <td className="apm-td apm-td--mono">
                      {l.expiryDate
                        ? <span className={isExp ? 'apm-expired' : ''}>{new Date(l.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        : <span className="apm-lifetime">∞</span>}
                    </td>
                    <td className="apm-td">
                      <span className={`apm-status${isActive ? ' apm-status--active' : ' apm-status--inactive'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="apm-td apm-td--actions">
                      <button className="apm-icon-btn" onClick={() => setEditTarget(l)} title="Edit"><EditIcon /></button>
                      <button className="apm-icon-btn apm-icon-btn--danger" onClick={() => setDeleteTarget(l)} title="Delete"><TrashIcon /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filtered.length > PAGE_SIZE && (
        <div className="apm-pagination">
          <button className="apm-page-btn" onClick={() => setPage(0)} disabled={currentPage === 0}>«</button>
          <button className="apm-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>‹</button>
          <span className="apm-page-info">{currentPage + 1} / {totalPages}</span>
          <button className="apm-page-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>›</button>
          <button className="apm-page-btn" onClick={() => setPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>»</button>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {editTarget && (
          <EditModal
            key="edit"
            license={editTarget}
            onClose={() => setEditTarget(null)}
            onSave={handleSave}
          />
        )}
        {deleteTarget && (
          <DeleteModal
            key="delete"
            license={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={() => handleDelete(deleteTarget.id)}
          />
        )}
        {showCreate && (
          <CreateModal
            key="create"
            onClose={() => setShowCreate(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
